// File: /src/components/PersonalDataForm.js

import React, { useState, useEffect } from 'react';
import { cpf, cnpj } from 'cpf-cnpj-validator';
import PhoneInput from 'react-phone-input-2';
import { countryTranslations } from '../utils/countryTranslations';
import { getOrderedCountries } from '../utils/countryUtils';
import 'react-phone-input-2/lib/style.css';

export const convertTranslationsForPhoneInput = (translations) => {
	const converted = {};
	Object.entries(translations).forEach(([code, countryData]) => {
		converted[code] = countryData.display;
	});
	return converted;
};

const sortCountryCodes = (countries) => {
	return countries.map((country) => country.code);
};

function PersonalDataForm({
	userData,
	cpfLocked,
	emailError,
	handleChange,
	handleDateChange,
	handlePersonalSubmit,
}) {
	const [dateError, setDateError] = useState('');
	const [dateInputValue, setDateInputValue] = useState(
		userData.birthOrFoundationDate
			? userData.birthOrFoundationDate.toLocaleDateString('pt-BR')
			: ''
	);

	// Estado local para o campo CPF/CNPJ, para formatação imediata
	const [cpfCnpjInput, setCpfCnpjInput] = useState(userData.cpf_cnpj || '');
	const [cpfCnpjError, setCpfCnpjError] = useState('');

	// Estado para o telefone
	const [phone, setPhone] = useState(userData.phone || '');

	// Estado para o CEP
	const [cep, setCep] = useState(userData.cep || '');
	const [cepError, setCepError] = useState('');
	const [isLoadingCep, setIsLoadingCep] = useState(false);

	// Força a reinicialização do país padrão quando o componente monta
	useEffect(() => {
		if (!phone) {
			setPhone('+55');
		}
	}, [phone]);

	// Formata a data enquanto o usuário digita
	const formatDateInput = (value) => {
		const digits = value.replace(/\D/g, '');
		let formatted = digits;
		if (digits.length > 2) {
			formatted = digits.slice(0, 2) + '/' + digits.slice(2);
		}
		if (digits.length > 4) {
			formatted =
				digits.slice(0, 2) +
				'/' +
				digits.slice(2, 4) +
				'/' +
				digits.slice(4, 8);
		}
		return formatted;
	};

	const validateDate = (dateStr) => {
		if (dateStr.length === 0) return true;
		const regex = /^(\d{2})\/(\d{2})\/(\d{4})$/;
		if (!regex.test(dateStr)) return false;
		const [, day, month, year] = dateStr.match(regex);
		const date = new Date(year, month - 1, day);
		return (
			date &&
			date.getFullYear() === Number(year) &&
			date.getMonth() === Number(month) - 1 &&
			date.getDate() === Number(day)
		);
	};

	const handleDateInputChange = (e) => {
		const value = e.target.value;
		const formatted = formatDateInput(value);
		setDateInputValue(formatted);

		if (!validateDate(formatted)) {
			setDateError('Data inválida');
			handleDateChange(null);
		} else {
			setDateError('');
			if (formatted.length === 10) {
				const [day, month, year] = formatted.split('/');
				const date = new Date(year, month - 1, day);
				handleDateChange(date);
			}
		}
	};

	// Função para formatar CPF ou CNPJ conforme o usuário digita
	const formatCpfCnpjInput = (value) => {
		const digits = value.replace(/\D/g, '');
		if (digits.length <= 11) {
			// Formatação para CPF: 000.000.000-00
			let cpfFormatted = '';
			if (digits.length > 0) {
				cpfFormatted = digits.substring(0, Math.min(3, digits.length));
			}
			if (digits.length >= 4) {
				cpfFormatted +=
					'.' + digits.substring(3, Math.min(6, digits.length));
			}
			if (digits.length >= 7) {
				cpfFormatted +=
					'.' + digits.substring(6, Math.min(9, digits.length));
			}
			if (digits.length >= 10) {
				cpfFormatted +=
					'-' + digits.substring(9, Math.min(11, digits.length));
			}
			return cpfFormatted;
		} else {
			// Formatação para CNPJ: 00.000.000/0000-00
			let cnpjFormatted = '';
			if (digits.length > 0) {
				cnpjFormatted = digits.substring(0, Math.min(2, digits.length));
			}
			if (digits.length >= 3) {
				cnpjFormatted +=
					'.' + digits.substring(2, Math.min(5, digits.length));
			}
			if (digits.length >= 6) {
				cnpjFormatted +=
					'.' + digits.substring(5, Math.min(8, digits.length));
			}
			if (digits.length >= 9) {
				cnpjFormatted +=
					'/' + digits.substring(8, Math.min(12, digits.length));
			}
			if (digits.length >= 13) {
				cnpjFormatted +=
					'-' + digits.substring(12, Math.min(14, digits.length));
			}
			return cnpjFormatted;
		}
	};

	// Atualiza o estado local enquanto o usuário digita
	const handleCpfCnpjChange = (e) => {
		const { value } = e.target;
		const formatted = formatCpfCnpjInput(value);
		setCpfCnpjInput(formatted);
		// Limpa o erro enquanto digita
		setCpfCnpjError('');
	};

	// Ao sair do campo, atualiza o estado global e valida
	const handleCpfCnpjBlur = (e) => {
		// Atualiza o estado global com o valor formatado
		handleChange({ target: { name: e.target.name, value: cpfCnpjInput } });

		// Valida o CPF/CNPJ utilizando somente os dígitos
		const digits = cpfCnpjInput.replace(/\D/g, '');
		let isValid = false;
		if (digits.length <= 11) {
			isValid = cpf.isValid(digits);
		} else {
			isValid = cnpj.isValid(digits);
		}
		if (!isValid) {
			setCpfCnpjError('CPF/CNPJ inválido.');
		}
	};

	// Atualiza o estado local se o valor vindo do pai mudar
	useEffect(() => {
		setCpfCnpjInput(userData.cpf_cnpj || '');
	}, [userData.cpf_cnpj]);

	// Atualiza o telefone quando userData mudar
	useEffect(() => {
		setPhone(userData.phone || '');
	}, [userData.phone]);

	// Calcula o tipo (CPF ou CNPJ) diretamente do estado local para feedback imediato
	let typeDetected = '';
	if (cpfCnpjInput) {
		const digits = cpfCnpjInput.replace(/\D/g, '');
		if (cpf.isValid(digits)) {
			typeDetected = 'pf';
		} else if (cnpj.isValid(digits)) {
			typeDetected = 'pj';
		}
	}

	// Intercepta o envio do formulário para garantir que o CPF/CNPJ seja válido
	const handleFormSubmit = (e) => {
		e.preventDefault();
		const digits = cpfCnpjInput.replace(/\D/g, '');
		let isValid = false;
		if (digits.length <= 11) {
			isValid = cpf.isValid(digits);
		} else {
			isValid = cnpj.isValid(digits);
		}
		if (!isValid) {
			setCpfCnpjError('CPF/CNPJ inválido.');
			return;
		}
		// Se válido, chama o submit recebido do pai com o valor formatado
		handlePersonalSubmit(e);
	};

	// Handler para o telefone
	const handlePhoneChange = (value, country) => {
		// Remove todos os caracteres não numéricos
		const numberOnly = value.replace(/\D/g, '');

		// Formata o número para o Brasil
		let formatted = value;
		if (country.countryCode === 'br') {
			const ddd = numberOnly.slice(2, 4);
			const numero = numberOnly.slice(4);

			if (ddd) {
				if (numero.length <= 8) {
					// Formato fixo: +55 (XX) XXXX-XXXX
					const primeiraParte = numero.slice(0, 4);
					const segundaParte = numero.slice(4);
					formatted = `+55 (${ddd}) ${primeiraParte}${
						numero.length > 4 ? '-' : ''
					}${segundaParte}`;
				} else {
					// Formato celular: +55 (XX) XXXXX-XXXX
					const primeiraParte = numero.slice(0, 5);
					const segundaParte = numero.slice(5);
					formatted = `+55 (${ddd}) ${primeiraParte}${
						numero.length > 5 ? '-' : ''
					}${segundaParte}`;
				}
			} else {
				formatted = `+55 (${numberOnly.slice(2)}`;
			}
		}

		setPhone(formatted);
		handleChange({ target: { name: 'phone', value: formatted } });
	};

	const formatCep = (value) => {
		return value.replace(/\D/g, '').replace(/(\d{5})(\d{3})/, '$1-$2');
	};

	const handleCepChange = (e) => {
		const rawValue = e.target.value.replace(/\D/g, '');
		const formattedCep = formatCep(rawValue);
		setCep(formattedCep);
		setCepError('');
		// Atualiza também o estado global
		handleChange({ target: { name: 'cep', value: formattedCep } });

		if (rawValue.length === 8) {
			searchCep(rawValue);
		}
	};

	const searchCep = async (rawCep) => {
		setIsLoadingCep(true);
		try {
			const response = await fetch(
				`https://viacep.com.br/ws/${rawCep}/json/`
			);
			const data = await response.json();

			if (data.erro) {
				setCepError('CEP não encontrado');
				return;
			}

			// Atualiza os campos de endereço
			handleChange({
				target: {
					name: 'address',
					value: data.logradouro,
				},
			});
			handleChange({
				target: {
					name: 'city',
					value: data.localidade,
				},
			});
			handleChange({
				target: {
					name: 'state',
					value: data.uf,
				},
			});
		} catch (error) {
			setCepError('Erro ao buscar CEP');
		} finally {
			setIsLoadingCep(false);
		}
	};

	return (
		<div className="personal-data-section">
			<h2>Meus Dados</h2>
			<form
				onSubmit={handleFormSubmit}
				className="user-account-form"
				autoComplete="off"
			>
				<div className="form-row">
					<label>
						CPF/CNPJ:
						<input
							type="text"
							name="cpf_cnpj"
							value={cpfCnpjInput}
							onChange={handleCpfCnpjChange}
							onBlur={handleCpfCnpjBlur}
							disabled={cpfLocked}
							placeholder="Digite seu CPF ou CNPJ"
						/>
					</label>
					{cpfCnpjError ? (
						<p style={{ color: 'red', fontSize: '0.9rem' }}>
							{cpfCnpjError}
						</p>
					) : (
						<p style={{ fontSize: '0.9rem', color: '#666' }}>
							{typeDetected === 'pf'
								? 'CPF detectado'
								: typeDetected === 'pj'
								? 'CNPJ detectado'
								: 'Aguardando...'}
						</p>
					)}
				</div>

				<div className="form-row">
					<label>
						Nome Completo:
						<input
							type="text"
							name="name"
							value={userData.name || ''}
							onChange={handleChange}
							placeholder="Digite seu nome completo"
						/>
					</label>
				</div>

				<div className="form-row">
					<label>
						E-mail:
						<input
							type="email"
							name="email"
							value={userData.email || ''}
							onChange={handleChange}
							placeholder="Digite seu e-mail"
							autoComplete="off"
							required
						/>
					</label>
					{emailError && (
						<p style={{ color: 'red', fontSize: '0.9rem' }}>
							{emailError}
						</p>
					)}
				</div>

				{typeDetected === 'pf' && (
					<div className="form-row">
						<label>
							Data de Nascimento:
							<div className="date-input-container">
								<input
									type="text"
									placeholder="DD/MM/AAAA"
									value={dateInputValue}
									onChange={handleDateInputChange}
									autoComplete="off"
								/>
								{dateError && (
									<span className="date-error">
										{dateError}
									</span>
								)}
							</div>
						</label>
					</div>
				)}

				<div className="form-row">
					<label>
						Telefone:
						<PhoneInput
							country="br"
							defaultCountry="br"
							value={phone}
							onChange={handlePhoneChange}
							inputProps={{
								required: true,
								autoComplete: 'off',
							}}
							countryCodeEditable={false}
							enableSearch={true}
							disableCountryCode={false}
							specialLabel=""
							inputStyle={{ width: '100%', height: '42px' }}
							containerStyle={{ width: '100%' }}
							dropdownStyle={{ width: '300px' }}
							searchPlaceholder="Buscar país..."
							searchNotFound="País não encontrado"
							preferredCountries={['br', 'us', 'pt']}
							priority={{ br: 0 }}
							localization={convertTranslationsForPhoneInput(
								countryTranslations
							)}
							onlyCountries={sortCountryCodes(
								getOrderedCountries()
							)}
						/>
					</label>
				</div>

				<div className="form-row">
					<label>
						CEP:
						<input
							type="text"
							name="cep"
							value={cep}
							onChange={handleCepChange}
							placeholder="00000-000"
							maxLength="9"
						/>
					</label>
					{cepError && <p className="error-message">{cepError}</p>}
					{isLoadingCep && <p>Buscando CEP...</p>}
				</div>

				<div className="form-row address-group">
					<div className="address-street">
						<label>
							Endereço:
							<input
								type="text"
								name="address"
								value={userData.address || ''}
								onChange={handleChange}
								placeholder="Rua/Avenida"
							/>
						</label>
					</div>
					<div className="number-complement-row">
						<div className="address-number">
							<label>
								Número:
								<input
									type="text"
									name="number"
									value={userData.number || ''}
									onChange={handleChange}
									placeholder="Nº"
								/>
							</label>
						</div>
						<div className="address-complement">
							<label>
								Complemento:
								<input
									type="text"
									name="complement"
									value={userData.complement || ''}
									onChange={handleChange}
									placeholder="Apto, Sala, etc."
								/>
							</label>
						</div>
					</div>
				</div>

				<div className="form-row location-group">
					<div className="city">
						<label>
							Cidade:
							<input
								type="text"
								name="city"
								value={userData.city || ''}
								onChange={handleChange}
								placeholder="Cidade"
							/>
						</label>
					</div>
					<div className="state">
						<label>
							Estado:
							<input
								type="text"
								name="state"
								value={userData.state || ''}
								onChange={handleChange}
								placeholder="UF"
								maxLength="2"
							/>
						</label>
					</div>
				</div>

				<div className="form-row">
					<button type="submit">Salvar</button>
				</div>
			</form>
		</div>
	);
}

export default PersonalDataForm;
